<template>
  <div class="sivi-message-component" v-bind:class="{ loaded : loaded }">
    <div v-if="siviMessage && loaded">
      <h4>{{ title ? title : siviMessage.label }}</h4>

      <div v-if="siviMessage.entities.length > 0">
        <a-tabs
            :default-active-key="0"
            :tab-bar-style="{height: '5rem'}"
            tab-position="top"
            @change="onTabChange">
          <a-tab-pane v-for="(entity, key) in siviMessage.entities"
                      :tab="entity.description" :key="key">
            <SiviEntityComponent :entity="entity"/>
          </a-tab-pane>
        </a-tabs>
      </div>

      <a-collapse v-if="siviMessage.subMessages" style="margin-top: 1.5rem;">
        <a-collapse-panel v-for="(message, key) in siviMessage.subMessages"
                          :key="key"
                          :header="getMessageTitle(message)">
          <div v-if="message.entities.length > 0">
            <a-tabs :default-active-key="0" @change="onTabChange" :tab-bar-style="{height: '50px'}">
              <a-tab-pane v-for="(entity, key) in message.entities"
                          :tab="entity.description" :key="key">
                <SiviEntityComponent :entity="entity"/>
              </a-tab-pane>
            </a-tabs>
          </div>
        </a-collapse-panel>
      </a-collapse>

      <a-form-item label="Bijlagen uit dit bericht opslaan bij een maatschappij"
                   v-if="attachmentsSavable && this.hasAttachments">
        <link-sivi-message-attachments-overview-component
            :afd-message-id="afdMessageId"
            :policyMessage="siviMessage"
            :save-attachments-open="saveAttachmentsOpen"
        />
      </a-form-item>
    </div>
    <a-button v-if="canReapplyToPolicy" @click="reapplyToPolicy" type="primary">opnieuw toepassen op polis</a-button>
    <div v-if="!loaded">
      <Skeleton class="skeleton" v-bind:loading="true"
                style="width: 100%;height: 46px;display: block; margin-bottom: 1px;"></Skeleton>
    </div>
    <a-alert v-if="hasError" message="Er ging iets mis tijdens het ophalen van het AFD bericht" type="error"
             show-icon></a-alert>
  </div>
</template>

<script>
import siviApi from "~/api/sivi";
import SiviEntityComponent from "~/modules/sivi/components/SiviEntityComponent";
import LinkSiviMessageAttachmentsOverviewComponent from "./LinkSiviMessageAttachmentsOverviewComponent";
import store from "../../../store";
import siviModule from "../store";
import SIVIEntityLabelEnum from "../../../enums/SIVIEntityLabelEnum";
import { Skeleton } from "vue-loading-skeleton";

export default {
  name: "SiviMessageComponent",
  props: {
    afdMessageId: {
      type: [String, Number],
      default: null
    },
    sourceSiviMessage: {
      type: Object,
      default: null
    },
    title: {
      type: String,
      default: null
    },
    attachmentsSavable: {
      type: Boolean,
      default: false
    },
    saveAttachmentsOpen: {
      type: Boolean,
      default: false
    },
    canReapplyToPolicy: {
      type: Boolean,
      default: false
    }
  },
  components: {
    SiviEntityComponent,
    LinkSiviMessageAttachmentsOverviewComponent,
    Skeleton
  },
  data () {
    return {
      siviMessage: null,
      attachments: [],
      loaded: false,
      hasError: false
    }
  },
  computed: {
    hasAttachments () {
      return (this.attachments.length > 0)
    },
  },
  created () {
    if (!store.hasModule('sivi')) {
      store.registerModule('sivi', siviModule)
    }

    if (this.sourceSiviMessage === null && this.afdMessageId === null) {
      throw new Error('Either the sivi message or the afd message id must be provided via the props')
    } else if (this.sourceSiviMessage !== null) {
      this.siviMessage = this.sourceSiviMessage
    }
  },
  mounted () {
    this.onInit()
  },
  methods: {
    async onInit () {
      if (this.siviMessage === null) {
        await siviApi.getByAfdMessageId(this.afdMessageId)
            .then((data) => {
              this.siviMessage = data.data
              this.extractAttachments(this.siviMessage)
            })
            .catch(error => {
              this.hasError = true
            })
      } else {
        this.extractAttachments(this.siviMessage)
      }

      this.loaded = true
    },
    getMessageTitle (message) {
      let messageTitle = message.label
      let attributes = this.getAttributesFromMessageByLabel(message, 'BYLSRT')

      if (attributes.length > 0) {
        messageTitle += ' (' + attributes.map(attribute => attribute.codeListDescription).join(', ') + ') '
      }

      return messageTitle
    },
    getAttributesFromEntityByLabel (entity, label) {
      let attributes = []
      for (let attributeKey in entity.attributes) {
        let attribute = entity.attributes[attributeKey]

        if (attribute.label === label) {
          attributes.push(attribute)
        }
      }

      if (typeof entity.entities !== 'undefined' && Array.isArray(entity.entities)) {
        for (let entityKey in entity.entities) {
          let subEntity = entity.entities[entityKey]
          attributes = attributes.concat(this.getAttributesFromEntityByLabel(subEntity, label))
        }
      }

      return attributes
    },
    getAttributesFromMessageByLabel (message, label) {
      let attributes = []
      for (let entityKey in message.entities) {
        let entity = message.entities[entityKey]
        attributes = attributes.concat(this.getAttributesFromEntityByLabel(entity, label))
      }

      if (typeof message.subMessages !== 'undefined' && Array.isArray(message.subMessages)) {
        for (let subMessageKey in message.subMessages) {
          let subMessage = message.subMessages[subMessageKey]
          attributes = attributes.concat(this.getAttributesFromMessageByLabel(subMessage, label))
        }
      }

      return attributes
    },
    onTabChange () {

    },
    extractAttachments (siviMessage) {
      if (siviMessage.entities.length > 0) {
        siviMessage.entities.forEach(entity => {
          entity.entities.forEach(subEntity => {
            if (subEntity.label === SIVIEntityLabelEnum.ATTACHMENT) {
              if (subEntity.attributes.find(attribute => {
                return attribute.label === SIVIEntityLabelEnum.FILE_DATA;
              })) {
                this.attachments.push(subEntity);
              }
            }
            if (subEntity.entities.length > 0) {
              if ((subEntity.entities && subEntity.entities.length > 0) || (subEntity.subMessages && subEntity.subMessages.length > 0)) {
                this.extractAttachments(subEntity)
              }
            }
          })
        })
      }

      if (siviMessage.subMessages.length > 0) {
        siviMessage.subMessages.forEach(subMessage => {
          this.extractAttachments(subMessage)
        })
      }

    },
    async reapplyToPolicy() {
      this.$confirm({
        content: "Weet je het zeker? Deze bewerking is niet meer terug te draaien.",
        okText: "Toepassen",
        cancelText: "Annuleren",
        onOk: async () => {
          try {
            let response = await siviApi.reapplyAfdMessage(this.afdMessageId);

            window.location.href = response.redirectUrl;
          } catch (e) {
            this.$notification.error({
              message: "Er iets mis gegaan met het opnieuw toepassen van het AFD bericht.",
              description: "Probeer het nog eens.",
            });
          }
        },
        onCancel: () => {
        },
      });
    }
  }
}
</script>

<style scoped>
.csv-export-component {
}

table.table th, table.table td {
  padding: 1.5rem 1rem;
}

</style>
